<template>
  <div class="page-wrapper">
    <div class="page-header">
      <div class="container">
        <div class="page-header-inner">
          <div class="new-order__steps">
            <div class="new-order__step" :class="{'new-order__step--active': step === 1}" @click="changeStep(1)">
              <div class="new-order__step-number">1</div>
              <span class="new-order__step-label">General Info</span>
            </div>
            <div class="new-order__step-line new-order__step-line--solid"></div>
            <div class="new-order__step" :class="{'new-order__step--active': step === 2}" @click="changeStep(2)">
              <div class="new-order__step-number">2</div>
              <span class="new-order__step-label">Select Service</span>
            </div>
            <div class="new-order__step-line"></div>
            <div class="new-order__step" :class="{'new-order__step--active': step === 3}" @click="changeStep(3)">
              <div class="new-order__step-number">3</div>
              <span class="new-order__step-label">Confirmation</span>
            </div>
          </div>
          <div class="new-order__header-title">Booking New Order</div>
        </div>
      </div>
    </div>
    <div class="page-main">
      <div class="container">
        <div class="page-main-inner">
          <Loader v-if="isLoading" />
          <Customer v-if="step === 1" />
          <Service v-if="step === 2" />
          <Review v-if="step === 3" />
          <div class="new-order__footer">
            <div
              class="new-order__button"
              :class="{
                'new-order__button--blue': nextBtnActive,
                'new-order__button--disabled': !nextBtnActive,
                'new-order__button--green': step === 3
              }"
              @click="nextStep"
            >
              <span class="new-order__button-text">{{ btnText }}</span>
              <i class="ri-arrow-right-circle-fill"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Customer from './Customer'
import Service from '../Service'
import Review from '../Review'
import StatusModal from '../Status'
import Loader from '@/components/Loader'
import {mapGetters, mapState} from 'vuex'
import moment from 'moment'

export default {
  name: 'NewOrder',
  components: {Customer, Review, Service, Loader},
  data() {
    return {
      step: 1,
      steps: [
        {step: 1, access: true},
        {step: 2, access: false},
        {step: 3, access: false}
      ],
      btnText: 'Next: Choose Service',
      nextBtnActive: false,
      orderID: null,
      isLoading: false
    }
  },
  computed: {
    ...mapGetters({
      pickup: 'newOrder/pickup',
      dropOff: 'newOrder/dropOff',
      billing: 'newOrder/billing',
      employee: 'newOrder/employee',
      customerID: 'payment/customers/idCustomer',
      note: 'newOrder/note',
      file: 'newOrder/file',
      date: 'newOrder/date',
      serviceID: 'payment/serviceID'
    }),
    ...mapState({
      card: s => s.card.activeCard,
      userInfo: s => s.client.info
    }),
    // An employee is only choosable when the account actually has a list. A
    // customer linked as enterprise (rather than converted) has none, so
    // demanding one left the Next button permanently grey with an empty
    // dropdown and no way to satisfy it.
    employeeRequired() {
      return !!(this.userInfo && this.userInfo.employeeNames && this.userInfo.employeeNames.length)
    },
    stepOneReady() {
      return !!(this.pickup && this.dropOff && this.billing && (!this.employeeRequired || this.employee))
    }
  },
  watch: {
    step(s) {
      switch (s) {
        case 1: {
          this.btnText = 'Next: Choose Service'
          if (this.stepOneReady && this.step === 1) this.nextBtnActive = true
          else this.nextBtnActive = false
          break
        }
        case 2: {
          this.btnText = 'Next: Confirm'
          if (this.serviceID) this.nextBtnActive = true
          else this.nextBtnActive = false
          break
        }
        case 3: {
          this.btnText = 'Confirm & Book'
          break
        }
      }
    },
    pickup() {
      if (this.stepOneReady && this.step === 1) {
        this.nextBtnActive = true
        this.steps[1].access = true
      } else {
        this.nextBtnActive = false
        this.steps[1].access = false
      }
    },
    dropOff() {
      if (this.stepOneReady && this.step === 1) {
        this.nextBtnActive = true
        this.steps[1].access = true
      } else {
        this.nextBtnActive = false
        this.steps[1].access = false
      }
    },
    employee() {
      if (this.stepOneReady && this.step === 1) {
        this.nextBtnActive = true
        this.steps[1].access = true
      } else {
        this.nextBtnActive = false
        this.steps[1].access = false
      }
    },
    billing() {
      if (this.stepOneReady && this.step === 1) {
        this.nextBtnActive = true
        this.steps[1].access = true
      } else {
        this.nextBtnActive = false
        this.steps[1].access = false
      }
    },
    serviceID(id) {
      if (id) {
        this.nextBtnActive = true
        this.steps[2].access = true
      } else {
        this.nextBtnActive = false
        this.steps[2].access = false
      }
    }
  },
  async created() {
    if (this.billing && this.pickup && this.dropOff && this.step === 1) {
      this.nextBtnActive = true
      this.steps[1].access = true
    }
    await this.$store.dispatch('payment/customers/fetchCustomer')
  },
  methods: {
    nextStep() {
      // In-flight lock: a second tap while the booking request is running must
      // be a no-op — enterprise orders go straight to dispatch, so a duplicate
      // here is a duplicate delivery.
      if (this.isLoading) return
      if (this.nextBtnActive && this.step === 1) {
        this.step++
      } else if (this.nextBtnActive && this.step === 2) {
        this.step++
      } else if (this.nextBtnActive && this.step === 3) {
        this.createOrder()
      }
    },
    changeStep(step) {
      const access = this.steps.find(s => s.step === step).access
      if (access) {
        this.step = step
      }
    },
    async createOrder() {
      const order = new FormData()
      order.append('routeDate', moment(this.date).format('YYYY-MM-DD'))
      order.append('serviceID', this.serviceID)
      order.append('companyID', process.env.VUE_APP_COMPANY_ID)
      order.append('pickUpID', this.pickup.id)
      order.append('dropOffID', this.dropOff.id)
      order.append('billingID', this.billing.id)
      this.note ? order.append('notes', this.note) : null
      this.file ? order.append('attachment', this.file) : null
      this.employee ? order.append('employee', this.employee) : null

      this.isLoading = true
      try {
        const orderInfo = await this.$store.dispatch('orders/create', order)
        const result = orderInfo.data.result

        this.openStatusScreen(result)
        this.reset()
      } catch (e) {
        // Message surfaced by the layout's error watcher; release the lock.
      } finally {
        this.isLoading = false
      }
    },
    openStatusScreen(result) {
      this.$modal.display(
        StatusModal,
        {
          result,
          payment: this.billing.id === this.customerID
        },
        {
          name: 'StatusModal',
          transition: 'none',
          width: 400,
          height: 400,
          classes: 'modal modal-order-status'
        }
      )
    },
    reset() {
      this.step = 1
      this.steps = [
        {step: 1, access: true},
        {step: 2, access: false},
        {step: 3, access: false}
      ]
      this.orderID = null
      this.$store.commit('newOrder/reset')
      this.$store.commit('card/reset')
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'style';
</style>
